import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { Container } from '@/components/layout/Container';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import styles from './Header.module.css';

/** Routes on which the "Sign in" entry is redundant (user is already
 *  on the sign-in page). The entry point is omitted to keep the bar
 *  honest — there is no action left to take when you're there. */
const LOGIN_PATHS = new Set(['/login']);

export const Header: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // The brand link itself already routes to "/", so a separate "back
  // to home" control is only useful on inner pages. Hide it on "/" —
  // and on the home page the entire brand-as-back affordance is
  // what already exists, so this is purely a subtraction.
  const showBack = pathname !== '/';
  // Suppress the Sign in entry on the Sign in page itself — rendering
  // "Sign in" while on /login reads as a dead-end.
  const showSignIn = !LOGIN_PATHS.has(pathname);

  return (
    <header className={styles.root}>
      <Container size="wide">
        <div className={styles.inner}>
          <Link to="/" className={styles.brand} aria-label={t('common.brand')}>
            <span className={styles.brandMark} aria-hidden="true" />
            <span className={styles.brandText}>{t('common.brand')}</span>
          </Link>

          <div className={styles.actions}>
            {/* Language switcher — visible on every viewport. */}
            <LanguageSwitcher />

            {/* Desktop-only Sign in button (≥721px). Hidden on /login
                where showing "Sign in" is meaningless. */}
            {showSignIn && (
              <div className={styles.desktopOnly}>
                <Button
                  as="link"
                  to="/login"
                  size="sm"
                  variant="primary"
                  style={{ width: '80px' }}
                >
                  {t('common.nav.login')}
                </Button>
              </div>
            )}

            {/* Desktop-only Back button (≥721px). Sits AFTER the Sign
                in button on the right side of the actions row. Hidden
                on "/" where it would duplicate the brand's affordance. */}
            {showBack && (
              <Link
                to="/"
                className={`${styles.backLink} ${styles.desktopOnly}`}
              >
                {t('common.nav.back')}
              </Link>
            )}

            {/* Mobile Sign in icon (≤720px). Same hide-rule as desktop
                Sign in: omitted on /login. Reuses the existing ≡ CSS
                (`.menuIcon`) as the entry glyph. */}
            {showSignIn && (
              <Link
                to="/login"
                className={`${styles.iconButton} ${styles.mobileOnly}`}
                aria-label={t('common.nav.login')}
              >
                <span className={styles.menuIcon} aria-hidden="true" />
              </Link>
            )}

            {/* Mobile Back icon (≤720px). Mirror of the desktop Back
                button — same destination, same hide-rule on "/". */}
            {showBack && (
              <Link
                to="/"
                className={`${styles.iconButton} ${styles.mobileOnly}`}
                aria-label={t('common.nav.back')}
              >
                <span className={styles.backGlyph} aria-hidden="true">
                  &larr;
                </span>
              </Link>
            )}
          </div>
        </div>
      </Container>
    </header>
  );
};

Header.displayName = 'Header';